import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BookOpen, Users, CheckCircle, Award } from 'lucide-react';
import { toast } from 'sonner';
import CategoryFilter from '../components/CategoryFilter';
import useImageColor from '../hooks/useImageColor';

const CourseCard = ({ course, status, onEnroll }) => {
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);
  const imgUrl = (!imgError && course.image_url) ? course.image_url : 'https://images.unsplash.com/photo-1609599006353-e629aaabfeae?auto=format&fit=crop&q=80&w=800';
  const imgColor = useImageColor(imgUrl);

  const badgeStyle = imgColor
    ? { backgroundColor: imgColor.bg, color: imgColor.text }
    : {};

  return (
    <Card
      className="overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer"
      onClick={() => navigate(`/courses/${course.course_id}`)}
      data-testid={`course-card-${course.course_id}`}
    >
      <div className="h-48 bg-gray-200 relative">
        <img src={imgUrl} alt={course.title} className="w-full h-full object-cover" loading="lazy"
          onError={() => setImgError(true)} />
      </div>
      <div className="p-6">
        <div className="mb-3 flex justify-between items-center">
          <span
            className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${!imgColor ? 'bg-primary/10 text-primary' : ''}`}
            style={imgColor ? badgeStyle : undefined}
          >
            {course.category}
          </span>
          {course.level && (
            <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-accent/10 text-accent">
              {course.level}
            </span>
          )}
        </div>
        <h3 className="text-xl font-bold text-black mb-3">{course.title}</h3>
        <p className="text-black/70 mb-4 line-clamp-3">{course.description}</p>

        {status === 'completed' ? (
          <Button
            disabled
            className="w-full text-white cursor-default"
            style={{ backgroundColor: '#b8860b', opacity: 1 }}
            data-testid={`completed-course-${course.course_id}`}
          >
            <Award className="w-5 h-5 ml-2" />
            تم الإنجاز
          </Button>
        ) : status === 'active' ? (
          <Button
            disabled
            className="w-full bg-green-600 text-white cursor-default"
            style={{ opacity: 1 }}
            data-testid={`enrolled-course-${course.course_id}`}
          >
            <CheckCircle className="w-5 h-5 ml-2" />
            مُنضَم
          </Button>
        ) : (
          <Button
            onClick={(e) => { e.stopPropagation(); onEnroll(course.course_id); }}
            className="w-full bg-black hover:bg-black/85 text-white"
            data-testid={`enroll-course-${course.course_id}`}
          >
            <Users className="w-5 h-5 ml-2" />
            الانضمام للمساق
          </Button>
        )}
      </div>
    </Card>
  );
};

const CoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [levels, setLevels] = useState([]);
  const [enrollmentMap, setEnrollmentMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedCategory = searchParams.get('category') || null;
  const selectedLevel = searchParams.get('level') || null;

  const setSelectedCategory = (val) => {
    const p = new URLSearchParams(searchParams);
    if (val) p.set('category', val); else p.delete('category');
    setSearchParams(p, { replace: true });
  };
  const setSelectedLevel = (val) => {
    const p = new URLSearchParams(searchParams);
    if (val) p.set('level', val); else p.delete('level');
    setSearchParams(p, { replace: true });
  };

  useEffect(() => { loadCourses(); }, [selectedCategory, selectedLevel]);

  const loadCourses = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.set('category', selectedCategory);
      if (selectedLevel) params.set('level', selectedLevel);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const [coursesRes, myCoursesRes] = await Promise.all([
        api.get(`/courses${qs}`),
        api.get('/my-courses'),
      ]);
      setCourses(coursesRes.data);

      const map = {};
      myCoursesRes.data.forEach((c) => { map[c.course_id] = c.status || 'active'; });
      setEnrollmentMap(map);

      if (!selectedCategory && !selectedLevel) {
        setCategories([...new Set(coursesRes.data.map((c) => c.category).filter(Boolean))]);
        setLevels([...new Set(coursesRes.data.map((c) => c.level).filter(Boolean))]);
      }
    } catch {
      toast.error('فشل تحميل المساقات');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (courseId) => {
    try {
      await api.post(`/courses/${courseId}/enroll`);
      setEnrollmentMap((prev) => ({ ...prev, [courseId]: 'active' }));
      toast.success('تم التسجيل في المساق بنجاح');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل التسجيل في المساق');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg text-accent">جارٍ التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="courses-page">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-accent mb-4">المساقات العلمية</h1>
        <p className="text-lg text-muted-foreground">اختر المساق المناسب لك وابدأ رحلتك العلمية</p>
      </div>

      {(categories.length > 0 || levels.length > 0) && (
        <div className="flex justify-center gap-4 flex-wrap" dir="rtl">
          {categories.length > 0 && (
            <CategoryFilter
              categories={categories}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
            />
          )}
          {levels.length > 0 && (
            <CategoryFilter
              categories={levels}
              selectedCategory={selectedLevel}
              setSelectedCategory={setSelectedLevel}
              allLabel="جميع المراحل"
              testIdPrefix="level"
            />
          )}
        </div>
      )}

      {courses.length === 0 ? (
        <div className="text-center py-16">
          <BookOpen className="w-16 h-16 text-muted mx-auto mb-4 opacity-50" />
          <p className="text-lg text-muted-foreground">لا توجد مساقات متاحة حالياً</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => (
            <CourseCard
              key={course.course_id}
              course={course}
              status={enrollmentMap[course.course_id]}
              onEnroll={handleEnroll}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CoursesPage;
